import React from 'react';
import { Check, Copy, Globe2, HelpCircle, Mail, Server } from 'lucide-react';
import { Button, Select } from '@cloudsam/ui';

const ROUTES = [
    {
        value: 'platform_shared',
        title: 'SamSurveyor',
		note: 'Default SamSurveyor mail service',
		tooltip: 'Uses the standard SamSurveyor no-reply mail service. No domain or mail-server setup is required.',
        icon: Mail,
    },
    {
        value: 'hosted',
        title: 'Labelled',
        note: 'Use your sending domain while SamSurveyor manages delivery.',
		tooltip: 'Uses your domain in the From address while SamSurveyor sends and manages delivery. DNS authentication is required.',
        icon: Globe2,
    },
    {
        value: 'customer_owned',
        title: 'Your mail system',
        note: 'Authorize Microsoft 365, Google Workspace, or another SMTP server.',
		tooltip: 'Uses your Microsoft 365, Google Workspace, or SMTP mail system to send SamSurveyor survey invitations.',
        icon: Server,
    },
];

function Field({ label, hint, children }) {
    return (
        <label className="manager-field">
            <span>{label}</span>
            {children}
            {hint && <small>{hint}</small>}
        </label>
    );
}

function routeMode(configuration) {
    return configuration?.send_mode || '';
}

function RouteState({ route, state }) {
    const active = routeMode(state?.active) === route;
    const pending = routeMode(state?.pending) === route;
    if (!active && !pending) return null;
	const passed = pending && state?.pending?.verification_state === 'verified';
    return (
        <span className={`manager-status${pending ? ' manager-status--pending' : ''}`}>
			{passed ? 'Test passed' : pending ? 'Pending test' : 'In use'}
        </span>
    );
}

function DNSRecords({ pending }) {
    if (!pending?.dkim_public_key) return null;
    const records = [
        ['TXT', `${pending.dkim_selector}._domainkey.${pending.from_domain}`, pending.dkim_public_key],
        ['TXT', pending.from_domain, `v=spf1 ${pending.spf_mechanism} ~all`],
        ['TXT', `_dmarc.${pending.from_domain}`, 'v=DMARC1; p=quarantine; adkim=s; aspf=s'],
    ];
    return (
        <div className="manager-dns" aria-label="DNS records to publish">
            <div className="manager-dns__head">
                <strong>DNS records</strong>
                <span>Publish all three before running the delivery test.</span>
            </div>
            {records.map(([type, host, value]) => (
                <div className="manager-dns__row" key={host}>
                    <b>{type}</b>
                    <code>{host}</code>
                    <code>{value}</code>
                    <Button
                        variant="secondary"
                        iconOnly
                        aria-label={`Copy DNS value for ${host}`}
                        title="Copy value"
                        onClick={() => navigator.clipboard?.writeText(value)}
                    >
                        <Copy size={16} strokeWidth={1.5} />
                    </Button>
                </div>
            ))}
        </div>
    );
}

function LabelledFields({ form, set, disabled, state }) {
    return (
        <div className="manager-fieldset" role="group" aria-labelledby="labelled-mail-parameters">
            <h4 id="labelled-mail-parameters" className="manager-fieldset__title">Labelled mail parameters</h4>
            <div className="manager-fields manager-fields--two">
				<Field label="Sending domain" hint="For example: surveys.example.com">
                    <input className="manager-input" value={form.from_domain || ''} onChange={set('from_domain')} disabled={disabled} />
                </Field>
				<Field label="Mailbox name" hint="No reply from address">
                    <input className="manager-input" value={form.from_local_part || 'noreply'} onChange={set('from_local_part')} disabled={disabled} />
                </Field>
            </div>
            <DNSRecords pending={state?.pending?.send_mode === 'hosted' ? state.pending : null} />
        </div>
    );
}

function MicrosoftFields({ form, set, disabled, state }) {
    return (
        <>
            <div className="manager-fields manager-fields--two">
                <Field label="Microsoft tenant ID">
                    <input className="manager-input" value={form.microsoft_tenant_id || ''} onChange={set('microsoft_tenant_id')} disabled={disabled} />
                </Field>
                <Field label="Authorized mailbox">
                    <input type="email" className="manager-input" value={form.microsoft_mailbox || ''} onChange={set('microsoft_mailbox')} disabled={disabled} />
                </Field>
            </div>
            <div className="manager-note">
                <strong>Administrator authorization</strong>
                <p>Your Microsoft administrator grants SamSurveyor Mail.Send access restricted to this mailbox. SamSurveyor never asks for the Microsoft password or application secret.</p>
                {state?.microsoft_consent_url ? (
                    <a href={state.microsoft_consent_url} target="_blank" rel="noreferrer">Open Microsoft administrator consent</a>
                ) : (
                    <small>Save the tenant and mailbox first. The consent link appears when the SamSurveyor application is configured.</small>
                )}
            </div>
        </>
    );
}

function GoogleFields({ form, set, disabled, state }) {
    return (
        <>
            <div className="manager-fields manager-fields--two">
                <Field label="Workspace domain">
                    <input className="manager-input" value={form.google_workspace_domain || ''} onChange={set('google_workspace_domain')} disabled={disabled} />
                </Field>
                <Field label="Authorized mailbox">
                    <input type="email" className="manager-input" value={form.google_sender_mailbox || ''} onChange={set('google_sender_mailbox')} disabled={disabled} />
                </Field>
            </div>
            <div className="manager-note">
                <strong>Domain-wide delegation</strong>
                <p>Authorize the SamSurveyor client in Google Admin for the Gmail send scope shown below.</p>
				{state?.google_delegation_client_id && <span className="manager-technical-value">{state.google_delegation_client_id}</span>}
                <span className="manager-technical-value">{state?.google_delegation_scope || 'https://www.googleapis.com/auth/gmail.send'}</span>
                <small>SamSurveyor never asks for a Google password or service-account key.</small>
            </div>
        </>
    );
}

function SMTPFields({ form, set, disabled }) {
    return (
        <div className="manager-fields manager-fields--three">
            <Field label="SMTP host"><input className="manager-input" value={form.smtp_host || ''} onChange={set('smtp_host')} disabled={disabled} /></Field>
            <Field label="Port"><input type="number" className="manager-input" value={form.smtp_port || 587} onChange={set('smtp_port')} disabled={disabled} /></Field>
            <Field label="Security">
                <Select className="manager-input" value={form.smtp_security || 'starttls'} onChange={set('smtp_security')} disabled={disabled}>
                    <option value="starttls">STARTTLS</option>
                    <option value="tls">TLS</option>
                </Select>
            </Field>
            <Field label="Username"><input className="manager-input" value={form.smtp_username || ''} onChange={set('smtp_username')} disabled={disabled} /></Field>
            <Field label="Password" hint="Replace-only. It can never be revealed."><input type="password" className="manager-input" value={form.smtp_password || ''} onChange={set('smtp_password')} disabled={disabled} /></Field>
            <Field label="From address">
                <div className="manager-address">
                    <input className="manager-input" value={form.from_local_part || 'noreply'} onChange={set('from_local_part')} disabled={disabled} />
                    <span>@</span>
                    <input className="manager-input" value={form.from_domain || ''} onChange={set('from_domain')} disabled={disabled} />
                </div>
            </Field>
        </div>
    );
}

function OwnMailFields({ form, set, disabled, state }) {
    return (
        <div className="manager-fieldset" role="group" aria-labelledby="own-mail-parameters">
            <h4 id="own-mail-parameters" className="manager-fieldset__title">Your mail system parameters</h4>
            <Field label="Mail system">
                <Select className="manager-input" value={form.provider || 'microsoft365'} onChange={set('provider')} disabled={disabled}>
                    <option value="microsoft365">Microsoft 365</option>
                    <option value="google_workspace">Google Workspace</option>
                    <option value="smtp">Other SMTP server</option>
                </Select>
            </Field>
            {form.provider === 'microsoft365' && <MicrosoftFields form={form} set={set} disabled={disabled} state={state} />}
            {form.provider === 'google_workspace' && <GoogleFields form={form} set={set} disabled={disabled} state={state} />}
            {form.provider === 'smtp' && <SMTPFields form={form} set={set} disabled={disabled} />}
        </div>
    );
}

export function EmailSendingPanel({
    canAdmin,
    editing,
    onEditingChange,
    mode,
    onMode,
    form,
    onField,
    state,
    loading,
    saving,
	testing,
	testPassed,
    error,
    onSaveSending,
	onCancelSending,
}) {
    const locked = !!state?.editing_locked;
    const disabled = !editing || !canAdmin || locked || loading || saving;
    const set = (name) => (event) => onField(name, event.target.value);
    const choose = (next) => {
        onMode(next);
        if (next === 'platform_shared' || next === 'hosted') onField('provider', 'webhook');
        if (next !== 'platform_shared' && form.from_domain === 'samsurveyor.com') {
            onField('from_domain', '');
        }
        if (next === 'customer_owned' && !['microsoft365', 'google_workspace', 'smtp'].includes(form.provider)) {
            onField('provider', 'microsoft365');
        }
    };

    return (
        <div className="email-manager">
            {locked && <div className="manager-banner manager-banner--warning">A recipient batch is sending now. Settings unlock automatically when it finishes.</div>}
            {loading && <div className="manager-banner">Loading the organization’s email settings…</div>}

            <div className="email-route-list">
                {ROUTES.map((route) => {
                    const Icon = route.icon;
                    const selected = mode === route.value;
					const inputId = `email-route-${route.value}`;
                    return (
                        <section key={route.value} className={`email-route${selected ? ' is-selected' : ''}`}>
							<div className="email-route__choice">
                                <input
									id={inputId}
                                    type="radio"
                                    name="email-route"
                                    value={route.value}
                                    checked={selected}
                                    onChange={() => choose(route.value)}
                                    disabled={!editing || !canAdmin || locked}
                                />
								<label className="email-route__title" htmlFor={inputId}>
									<Icon size={20} strokeWidth={1.5} aria-hidden="true" />
                                    <strong>{route.title}</strong>
								</label>
								<button
									type="button"
									className="email-route__help"
									aria-label={`About ${route.title}`}
									title={route.tooltip}
								>
									<HelpCircle size={17} strokeWidth={1.5} aria-hidden="true" />
								</button>
                                <RouteState route={route.value} state={state} />
								<small className="email-route__note">{route.note}</small>
							</div>

                            {selected && (
                                <div className="email-route__details">
                                    {route.value === 'platform_shared' && (
                                        <div className="manager-note manager-note--success">
                                            <Check size={18} strokeWidth={1.5} aria-hidden="true" />
                                            <div>
                                                <strong>No setup required</strong>
												<p>From: &lt;account owner&gt; via SamSurveyor</p>
                                            </div>
                                        </div>
                                    )}
                                    {route.value === 'hosted' && <LabelledFields form={form} set={set} disabled={disabled} state={state} />}
                                    {route.value === 'customer_owned' && <OwnMailFields form={form} set={set} disabled={disabled} state={state} />}
                                </div>
                            )}
                        </section>
                    );
                })}
            </div>

            {error && <div className="manager-banner manager-banner--error">{error}</div>}

            {editing && <div className="manager-actions">
				<span>Use Play in the persistent controls to test this route. Save becomes available after it passes.</span>
                <div>
					<Button variant="secondary" disabled={saving || testing} onClick={() => { onEditingChange?.(false); onCancelSending?.(); }}>Cancel</Button>
					<Button variant="primary" disabled={disabled || !testPassed || testing} onClick={async () => {
						const saved = await onSaveSending?.();
						if (saved !== false) onEditingChange?.(false);
					}}>
						{saving ? 'Saving…' : 'Save'}
                    </Button>
                </div>
            </div>}
        </div>
    );
}

export default function EmailBindingPanel(props) {
    return <EmailSendingPanel {...props} />;
}
